import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import '../Admin/Users.css';
import { formatAppDate } from '../../utils/dateUtils';

const ExtrusionWorkOrderList = forwardRef(({ onSelectOrder, selectedOrderId, onNew }, ref) => {
  const { t } = useTranslation('production');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    refresh: fetchOrders
  }));

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let url = getApiUrl('/api/extrusions/orders?page_size=100');
      if (statusFilter !== 'all') {
        url += `&status=${statusFilter}`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      const res = await apiFetch(url);
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
      }
    } catch (e) {
      console.error('Error fetching extrusion orders:', e);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(o => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      (o.order_number || '').toLowerCase().includes(s) ||
      (o.bobbin_item_name || '').toLowerCase().includes(s) ||
      (o.bobbin_item_code || '').toLowerCase().includes(s)
    );
  });

  const getStatusBadge = (status) => {
    const map = {
      DRAFT: 'status-warning',
      APPROVED: 'status-info',
      MATERIALS_REQUESTED: 'status-info',
      IN_PROGRESS: 'status-info',
      COMPLETED: 'status-success',
      CANCELLED: 'status-error',
      VOIDED: 'status-error',
    };
    return <span className={`status-badge ${map[status] || 'status-inactive'}`}>{status ? t('extrusionWorkOrders.status.' + status, { defaultValue: status.replace(/_/g, ' ') }) : status}</span>;
  };

  const formatDate = (val) => {
    if (!val) return '-';
    return formatAppDate(val);
  };

  if (loading && orders.length === 0) {
    return <div className="list-container"><div className="loading">{t('extrusionWorkOrderList.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>🏭 {t('extrusionWorkOrderList.title')}</h2>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('extrusionWorkOrderList.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchOrders()}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('extrusionWorkOrderList.allStatus')}</option>
            <option value="DRAFT">{t('extrusionWorkOrders.status.DRAFT')}</option>
            <option value="APPROVED">{t('extrusionWorkOrders.status.APPROVED')}</option>
            <option value="MATERIALS_REQUESTED">{t('extrusionWorkOrders.status.MATERIALS_REQUESTED')}</option>
            <option value="IN_PROGRESS">{t('extrusionWorkOrders.status.IN_PROGRESS')}</option>
            <option value="COMPLETED">{t('extrusionWorkOrders.status.COMPLETED')}</option>
            <option value="CANCELLED">{t('extrusionWorkOrders.status.CANCELLED')}</option>
            <option value="VOIDED">{t('extrusionWorkOrders.status.VOIDED')}</option>
          </select>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('extrusionWorkOrderList.columns.orderNumber')}</th>
              <th>{t('extrusionWorkOrderList.columns.bobbinSku')}</th>
              <th>{t('extrusionWorkOrderList.columns.bobbins')}</th>
              <th>{t('common:ui.status')}</th>
              <th>{t('extrusionWorkOrderList.columns.prodDate')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 ? (
              <tr><td colSpan="5" className="no-data">{t('extrusionWorkOrderList.empty')}</td></tr>
            ) : (
              filteredOrders.map(o => (
                <tr
                  key={o.work_order_id}
                  className={selectedOrderId === o.work_order_id ? 'selected' : ''}
                  onClick={() => onSelectOrder(o.work_order_id)}
                >
                  <td><strong>{o.order_number}</strong></td>
                  <td>{o.bobbin_item_name || o.bobbin_item_code || '-'}</td>
                  <td>{o.planned_bobbins}</td>
                  <td>{getStatusBadge(o.status)}</td>
                  <td>{formatDate(o.production_date)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default ExtrusionWorkOrderList;
